import { useApolloClient } from "@apollo/client";
import {
  useCreateEncryptionContext,
  useInitiateSealdIdentity,
} from "apollo/hooks/mutations";
import { useGetCareprovider } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { PatientFilesProvider } from "containers/PatientFilesProvider";
import RootPage from "containers/RootPage";
import { useCareproviderAccountContext } from "context/CareproviderAccountContext";
import {
  CAREPROVIDER_TAB_DEFAULT,
  CARESEEKER_TYPE,
  REDIRECT_PARAM,
} from "core/consts";
import {
  hasAccessToAdmissions,
  hasAccessToAnalytics,
} from "core/model/accounts";
import api from "core/model/api";
import { hasAccessToReverseSearch } from "core/model/careproviders";
import { isProd } from "core/model/utils/featureFlags";
import { getQueryVariable, useIntParams } from "core/model/utils/urls";
import { useSealdContext } from "core/seald/SealdContext";
import { Careprovider, CareproviderTab } from "core/types";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import { useAllowPWA } from "dsl/atoms/BrowserNotificationContext/NotificationPermission";
import RedirectRoot from "dsl/atoms/RedirectRoot";
import lazy from "dsl/atoms/retry";
import ThrowError from "dsl/atoms/ThrowError";
import AuctionRequestHome from "dsl/ecosystems/AuctionRequest/ConnectAuctionRequest";
import {
  clearUpdatedRequest,
  getUpdatedRequest,
} from "dsl/ecosystems/AuctionRequest/updatedRequest";
import {
  AdmissionsContextProvider,
  AdmissionsPatientContextProvider,
  AdmissionsPreviewContextProvider,
} from "dsl/ecosystems/CareproviderAdmissions/AdmissionsContext";
import LogoutPage from "dsl/ecosystems/LogoutPage";
import { useScrollRestoreCheckLocation } from "dsl/hooks/useScrollRestoreCheckLocation";
import CareproviderAccountsProvider from "dsl/organisms/CareproviderAccountsProvider";
import { ComponentType, LazyExoticComponent, useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  useAppSelector,
  useConnecterWithLoggedAccount,
  useLoggedCareproviderId,
  useLoggedCareseeker,
  useLoggedInAccount,
} from "reduxentities/hooks";
import { selectLoggedCareseeker } from "reduxentities/selectors";
import { ReceiverContext } from "..";
import { AuthRoutes } from "./AuthRoutes";
import {
  DebugPage,
  getDefaultRedirectPath,
  RequireCondition,
  ValidateCareprovider,
} from "./helpers";
import { useAdmissionsSessionGuard } from "./useAdmissionsSessionGuard";

const DiagnosticPage = lazy(() => import("dsl/organisms/DiagnosticPage"));
const TestSeald = lazy(() => import("dsl/atoms/TestSeald"));
const PasswordResetPageV2 = lazy(
  () => import("dsl/organisms/PasswordResetPageV2"),
);
const PasswordChangePageV2 = lazy(
  () => import("dsl/ecosystems/PasswordChangePageV2"),
);
const ReceiverDashboard = lazy(() => import("dsl/organisms/ReceiverDashboard"));

const CareproviderDashboard = lazy(
  () => import("dsl/ecosystems/CareproviderDashboard"),
);
const lazyPreload = <Props extends object>(
  importStatement: () => Promise<{ default: ComponentType<Props> }>,
) => {
  const Component = lazy(importStatement) as LazyExoticComponent<
    ComponentType<Props>
  > & { preload: typeof importStatement };
  Component.preload = importStatement;
  return Component;
};
export const AuctionRequest = lazyPreload(
  () => import("dsl/ecosystems/AuctionRequest/connect"),
);
const BarthelSummaryPage = lazy(
  () =>
    import(
      "dsl/ecosystems/IndependenceScaleSummaryPage/BarthelSummaryPage/connected"
    ),
);
const SingerAssessmentSummaryPage = lazy(
  () =>
    import(
      "dsl/ecosystems/IndependenceScaleSummaryPage/SingerSummaryPage/connected"
    ),
);
const EarlyRehabSummaryPage = lazy(
  () =>
    import(
      "dsl/ecosystems/IndependenceScaleSummaryPage/EarlyRehabSummaryPage/connected"
    ),
);
const SettingsAccountPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsAccount"),
);
const SettingsCompanyProfilePage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsCompanyProfile"),
);
const SettingsServicesPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsServices"),
);
const SettingsTeamPageNew = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsTeamNew"),
);
const SettingsNotificationsPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsNotifications"),
);
const SettingsPWAPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsPWA"),
);
const SettingsPartnerClinicsPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsPartnerClinics"),
);
const SettingsLegalDocumentsPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsLegalDocuments"),
);
const SettingsExtensionsPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsExtensions"),
);
const SettingsCapacityPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsCapacity/index"),
);
const SettingsReverseSearchPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsReverseSearch"),
);
const NewOnboardingCareprovider = lazy(
  () => import("dsl/ecosystems/CareproviderOnboarding"),
);
const NewOnboardingCareproviderPage = lazy(
  () => import("dsl/ecosystems/CareproviderOnboarding/pages"),
);
const NewOnboardingPersonal = lazy(
  () => import("dsl/ecosystems/CareproviderOnboarding/PersonalOnboarding"),
);
const Settings = lazy(
  () => import("dsl/ecosystems/CareproviderSettings/index"),
);

const CareproviderProfile = lazy(
  () => import("dsl/ecosystems/CareproviderProfile"),
);
const CareproviderAccount = lazy(
  () => import("dsl/ecosystems/CareproviderAccount"),
);
const CareproviderNotificationPanel = lazy(
  () => import("dsl/ecosystems/CareproviderNotificationPanel"),
);
const ForgotPasswordPage = lazy(
  () => import("dsl/ecosystems/ForgotPasswordPage"),
);

const SettingsAdmissionsPage = lazy(
  () => import("dsl/ecosystems/CareproviderSettingsAdmissions"),
);
const ReportPage = lazy(() => import("dsl/ecosystems/CareproviderReportPage"));

// Admissions
const AdmissionsLoginPage = lazy(
  () => import("dsl/ecosystems/CareproviderAdmissions/Login"),
);
const AdmissionsLoginWizard = lazy(
  () => import("dsl/ecosystems/AdmissionsWizard"),
);
const AdmissionsForm = lazy(
  () => import("dsl/ecosystems/CareproviderAdmissions/Form"),
);
const AdmissionsInfoScreens = lazy(
  () => import("dsl/ecosystems/CareproviderAdmissions/InfoScreens"),
);
const AdmissionsActionScreens = lazy(
  () => import("dsl/ecosystems/CareproviderAdmissions/ActionScreens"),
);

type CareproviderDashboardContext = {
  careprovider: Careprovider;
  token: string | undefined;
  updatedRequest: number | undefined;
};

export function useCareproviderDashboardContext() {
  return useOutletContext<CareproviderDashboardContext>();
}

const authPath = "/careprovider/auth";

export { AuthRoutes } from "./AuthRoutes";

function ProtectedRoutes({ context }: { context: ReceiverContext }) {
  const apolloClient = useApolloClient();
  const { careproviderId } = useIntParams<{ careproviderId: number }>({
    careproviderId: -1,
  });
  const token = getQueryVariable(location.search, "token");
  const toAuth = {
    pathname: authPath,
    search: `${REDIRECT_PARAM}=${encodeURIComponent(
      `${location.pathname}${location.search}`,
    )}&careproviderid=${careproviderId}${token ? `&token=${token}` : ""}`,
  };

  const isAuthenticated = context.checkAuth(careproviderId, apolloClient);
  const careproviderInStore = useLoggedCareproviderId();

  if (!isAuthenticated || !careproviderInStore) {
    return <Navigate to={toAuth} />;
  }
  // handle account switching careprovider
  if (careproviderInStore !== careproviderId) {
    return (
      <Navigate replace to={getDefaultRedirectPath(careproviderInStore)} />
    );
  }
  return (
    <CareproviderAccountsProvider>
      <PatientFilesProvider>
        <DashboardPage />
      </PatientFilesProvider>
    </CareproviderAccountsProvider>
  );
}

function AdmissionsProtectedFormRoutes() {
  const account = useLoggedInAccount();
  const loggedCareseeker = useAppSelector(selectLoggedCareseeker);
  const fetchedCareseeker = useLoggedCareseeker({
    fetchPolicy: "cache-first",
  });
  const fetchedAccount = useConnecterWithLoggedAccount();
  const { admissionsId } = useParams<{ admissionsId: string }>();
  const [createEncryptionContext] = useCreateEncryptionContext();
  const [initiateSealdIdentity] = useInitiateSealdIdentity();
  const createSealdGroup = useSealdContext()?.createSealdGroup;

  const [sealdGroupInitialising, setSealdGroupInitialising] = useState(true);

  const isInvalid =
    !account ||
    !loggedCareseeker?.type ||
    loggedCareseeker.type !== CARESEEKER_TYPE.ADMISSIONS;

  useAdmissionsSessionGuard(isInvalid);

  useEffect(() => {
    async function createSealdGroupIfNeeded() {
      if (
        !isInvalid &&
        fetchedCareseeker?.id === loggedCareseeker.id &&
        fetchedAccount
      ) {
        if (createSealdGroup) {
          try {
            await createSealdGroup({
              createEncryptionContext,
              createSealdAccess: api.crypto.createSealdAccess,
              entity: fetchedCareseeker,
              entityAccounts: [fetchedAccount],
              entityType: "careseeker",
              initiateSealdIdentity,
              loggedAccount: fetchedAccount,
            });
          } catch (err) {
            console.error(err);
          }
        } else {
          console.error(
            "could not create seald group in AdmissionsProtectedFormRoutes as createSealdGroup not initialised",
          );
        }
        setSealdGroupInitialising(false);
      }
    }
    createSealdGroupIfNeeded();
  }, [
    isInvalid,
    fetchedCareseeker,
    createSealdGroup,
    createEncryptionContext,
    initiateSealdIdentity,
    fetchedAccount,
  ]);

  if (isInvalid) {
    return (
      <Navigate replace to={`/careprovider/admissions/${admissionsId}/login`} />
    );
  }

  if (sealdGroupInitialising) {
    return <SpinnerPage id="admissions-seald-group-initialising" />;
  }

  return <AdmissionsPatientContextProvider />;
}

function DashboardPage() {
  const { careproviderId } = useIntParams<{ careproviderId: number }>({
    careproviderId: -1,
  });
  const updatedRequest = getUpdatedRequest();
  clearUpdatedRequest();
  const token = getQueryVariable(location.search, "token");
  const [queryProgress, careprovider] = useGetCareprovider({
    id: careproviderId,
    queryOptions: { withRoles: true },
    token,
  });

  return (
    <Result
      data={careprovider}
      id="careprovider-dashboard-routes"
      LoadingComponent={
        <SpinnerPage height="100%" id="careprovider-dashboard-routes" />
      }
      queryProgress={queryProgress}
    >
      {(loadedCareprovider) => (
        <ValidateCareprovider careprovider={loadedCareprovider} token={token}>
          <Outlet
            context={{
              careprovider: loadedCareprovider,
              token,
              updatedRequest,
            }}
          />
        </ValidateCareprovider>
      )}
    </Result>
  );
}

function RequestRoutes({
  careprovider,
  updatedRequest,
}: {
  careprovider: Careprovider;
  updatedRequest: number | undefined;
}) {
  const { param } = useParams<{ param: string }>();
  const tabOrAuctionRequestId = parseInt(param as string);
  const isAuctionRequestId = !isNaN(tabOrAuctionRequestId);
  useScrollRestoreCheckLocation(["requests", "careprovider"]);

  return (
    <Routes>
      {isAuctionRequestId ? (
        <Route
          element={
            <AuctionRequestHome
              auctionRequestId={tabOrAuctionRequestId}
              careproviderId={careprovider.id}
            />
          }
        >
          <Route
            element={
              <AuctionRequest auctionRequestId={tabOrAuctionRequestId} />
            }
            index
          />
          <Route element={<BarthelSummaryPage />} path="barthel-index" />
          <Route
            element={<SingerAssessmentSummaryPage />}
            path="singer-index"
          />
          <Route element={<EarlyRehabSummaryPage />} path="early-rehab-index" />
          <Route
            element={
              <AuctionRequest auctionRequestId={tabOrAuctionRequestId} />
            }
            path=":page"
          />
          <Route
            element={<Navigate replace to={`${CAREPROVIDER_TAB_DEFAULT}`} />}
            path="*"
          />
        </Route>
      ) : (
        <>
          <Route
            element={
              <ReceiverDashboard
                careprovider={careprovider}
                routeTab={param as CareproviderTab}
                updatedRequest={updatedRequest}
              />
            }
            index
          />
        </>
      )}
    </Routes>
  );
}

function DashboardRoutesSwitch() {
  const { careprovider, token, updatedRequest } =
    useCareproviderDashboardContext();
  const { connected, hasAccounts, loggedAccount } =
    useCareproviderAccountContext();
  const allowPWA = useAllowPWA();
  const hasAnalyticsRole = hasAccessToAnalytics(loggedAccount, careprovider);
  const hasAdmissionsRole = hasAccessToAdmissions(loggedAccount, careprovider);

  return (
    <Routes>
      <Route
        element={
          <RequireCondition hasCondition={hasAdmissionsRole}>
            <AdmissionsPreviewContextProvider careprovider={careprovider}>
              <AdmissionsForm />
            </AdmissionsPreviewContextProvider>
          </RequireCondition>
        }
        path="admissions/form/:form/step/:step"
      />
      <Route element={<CareproviderDashboard careprovider={careprovider} />}>
        <Route
          element={
            <RequireCondition hasCondition={!isProd}>
              <TestSeald careprovider={careprovider} />
            </RequireCondition>
          }
          path="testseald"
        />

        <Route
          element={<Settings careprovider={careprovider} />}
          path="settings/*"
        >
          <Route
            element={<SettingsAccountPage careproviderId={careprovider.id} />}
            path="account"
          />
          <Route
            element={<SettingsCompanyProfilePage careprovider={careprovider} />}
            path="profile"
          />
          <Route
            element={<SettingsServicesPage careprovider={careprovider} />}
            path="services"
          />
          <Route
            element={<SettingsTeamPageNew careprovider={careprovider} />}
            path="team"
          />
          <Route
            element={<SettingsNotificationsPage careprovider={careprovider} />}
            path="notifications"
          />
          <Route
            element={
              <RequireCondition hasCondition={allowPWA}>
                <SettingsPWAPage />
              </RequireCondition>
            }
            path="pwa"
          />
          <Route
            element={<SettingsPartnerClinicsPage careprovider={careprovider} />}
            path="partner-clinics"
          />
          <Route
            element={<SettingsLegalDocumentsPage careprovider={careprovider} />}
            path="legal-documents"
          />
          {/* Redirect for backward compatibility - TODO: Remove this route after 2025-09-01 */}
          <Route
            element={<Navigate replace to="legal-documents" />}
            path="legal_documents"
          />
          <Route
            element={<SettingsExtensionsPage careprovider={careprovider} />}
            path="extensions/*"
          />
          <Route
            element={<SettingsCapacityPage careprovider={careprovider} />}
            path="capacity"
          />
          <Route
            element={
              <RequireCondition hasCondition={hasAdmissionsRole}>
                <SettingsAdmissionsPage careprovider={careprovider} />
              </RequireCondition>
            }
            path="admissions"
          />

          <Route
            element={
              <RequireCondition
                hasCondition={hasAccessToReverseSearch(careprovider)}
              >
                <SettingsReverseSearchPage careprovider={careprovider} />
              </RequireCondition>
            }
            path="search"
          />
          <Route element={<Navigate replace to="account" />} path="*" />
        </Route>

        {/* handle deprecated emails providing this link */}
        <Route
          element={
            <Navigate replace to={getDefaultRedirectPath(careprovider.id)} />
          }
          path="recommendations"
        />
        <Route
          element={
            <RequireCondition hasCondition={!connected && !hasAccounts}>
              <CareproviderProfile careprovider={careprovider} token={token} />
            </RequireCondition>
          }
          path="profile"
        />
        <Route
          element={
            <RequireCondition hasCondition={!!connected}>
              <CareproviderAccount />
            </RequireCondition>
          }
          path="my_account"
        />
        <Route element={<PasswordChangePageV2 />} path="my-password-change" />
        <Route path="requests/*">
          <Route
            element={
              <RequestRoutes
                careprovider={careprovider}
                updatedRequest={updatedRequest}
              />
            }
            path={":param/*"}
          />
          <Route
            element={<Navigate to={`${CAREPROVIDER_TAB_DEFAULT}`} />}
            path="*"
          />
        </Route>
        <Route
          element={<CareproviderNotificationPanel />}
          path="notification-panel"
        />
        <Route
          element={
            <RequireCondition hasCondition={hasAnalyticsRole}>
              <ReportPage careprovider={careprovider} />
            </RequireCondition>
          }
          path="report/:tab"
        />
      </Route>
    </Routes>
  );
}

export default function CareproviderRoutes({ ...context }: ReceiverContext) {
  return (
    <Routes>
      <Route element={<RootPage appName="careprovider" />} path="/*">
        <Route element={<DiagnosticPage />} path="diagnostic" />
        <Route element={<ThrowError />} path="throwerror" />
        <Route element={<LogoutPage />} path="logout" />
        <Route
          element={<AdmissionsContextProvider />}
          path="careprovider/admissions/:admissionsId/*"
        >
          <Route element={<AdmissionsLoginWizard />} path="wizard" />
          <Route element={<AdmissionsLoginPage />} path="login" />
          <Route element={<AdmissionsProtectedFormRoutes />} path="step/*">
            <Route element={<AdmissionsForm />} path=":step" />
          </Route>
          <Route element={<AdmissionsInfoScreens />} path="info/:infoType" />
          <Route element={<RedirectRoot to="login" />} path="*" />
        </Route>
        <Route
          element={<AdmissionsActionScreens />}
          path="careprovider/admissions/:admissionsId/:action"
        />
        <Route path="careprovider/*">
          <Route element={<DebugPage />} path="info" />
          <Route element={<ForgotPasswordPage />} path="forgot-password" />
          <Route element={<AuthRoutes context={context} />} path="auth" />
          <Route path="newonboarding/*">
            <Route
              element={<NewOnboardingCareprovider />}
              path=":careproviderId/*"
            >
              <Route element={<NewOnboardingCareproviderPage />} index />
              <Route element={<NewOnboardingCareproviderPage />} path=":step" />
            </Route>
            <Route path="personal/:careproviderId/*">
              <Route element={<NewOnboardingPersonal />} index />
              <Route element={<NewOnboardingPersonal />} path=":step" />
            </Route>
          </Route>
          <Route
            element={<ProtectedRoutes context={context} />}
            path="dashboard/:careproviderId/*"
          >
            <Route element={<DashboardRoutesSwitch />} path="*" />
          </Route>
          <Route
            element={<PasswordResetPageV2 authRoute={authPath} />}
            path="reset/:token"
          />
          <Route element={<RedirectRoot to={authPath} />} path="*" />
        </Route>
        <Route element={<RedirectRoot to={authPath} />} path="*" />
      </Route>
    </Routes>
  );
}
